import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Package, CreditCard, Banknote, PiggyBank, Zap, FileSignature, Truck, ChevronRight } from 'lucide-react';
export function FinancingPage() {
  return <div className="min-h-screen bg-white flex flex-col" data-id="element-361">
      <Header data-id="element-362" />

      <main className="flex-grow pt-32 pb-0" data-id="element-363">
        {/* Hero Section */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-20" data-id="element-364">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 uppercase tracking-tight mb-6 leading-tight" data-id="element-365">
            Financing Helps
            <br data-id="element-366" />
            Your
            <br data-id="element-367" />
            Business Grow
          </h1>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto" data-id="element-368">
            With QuickSpark's simple financing, you can get the equipment you
            need for a low monthly payment you can afford. Quickly earn back
            your investment while maintaining a steady cash flow.
          </p>
          <Button className="bg-red-600 hover:bg-red-700 text-white px-10 py-4 text-lg font-bold uppercase tracking-wide rounded-sm" data-id="element-369">
            Apply Now
          </Button>
        </section>

        {/* ROI Process Flow */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-370">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8 md:gap-4 relative" data-id="element-371">
            {/* Step 1 */}
            <div className="flex-1 text-center relative z-10" data-id="element-372">
              <div className="w-24 h-24 mx-auto mb-4 flex items-center justify-center" data-id="element-373">
                <Package className="w-16 h-16 text-gray-800" strokeWidth={1.5} data-id="element-374" />
              </div>
              <div className="bg-gray-200 py-2 px-4 mb-4 font-bold text-gray-700 text-sm uppercase tracking-wide" data-id="element-375">
                New Equipment
              </div>
              <p className="text-sm text-gray-600 uppercase font-semibold" data-id="element-376">
                Let's say your new
                <br data-id="element-377" />
                equipment costs
                <br data-id="element-378" />
                <span className="text-red-600" data-id="element-379">$6,000.</span>
              </p>
            </div>

            <ChevronRight className="hidden md:block w-8 h-8 text-red-500 flex-shrink-0" data-id="element-380" />

            {/* Step 2 */}
            <div className="flex-1 text-center relative z-10" data-id="element-381">
              <div className="w-24 h-24 mx-auto mb-4 flex items-center justify-center" data-id="element-382">
                <CreditCard className="w-16 h-16 text-gray-800" strokeWidth={1.5} data-id="element-383" />
              </div>
              <div className="bg-gray-200 py-2 px-4 mb-4 font-bold text-gray-700 text-sm uppercase tracking-wide" data-id="element-384">
                Payment
              </div>
              <p className="text-sm text-gray-600 uppercase font-semibold" data-id="element-385">
                Your monthly
                <br data-id="element-386" />
                equipment payment is
                <br data-id="element-387" />
                <span className="text-red-600" data-id="element-388">$225.</span>
              </p>
            </div>

            <ChevronRight className="hidden md:block w-8 h-8 text-red-500 flex-shrink-0" data-id="element-389" />

            {/* Step 3 */}
            <div className="flex-1 text-center relative z-10" data-id="element-390">
              <div className="w-24 h-24 mx-auto mb-4 flex items-center justify-center" data-id="element-391">
                <Banknote className="w-16 h-16 text-gray-800" strokeWidth={1.5} data-id="element-392" />
              </div>
              <div className="bg-gray-200 py-2 px-4 mb-4 font-bold text-gray-700 text-sm uppercase tracking-wide" data-id="element-393">
                Earnings
              </div>
              <p className="text-sm text-gray-600 uppercase font-semibold" data-id="element-394">
                You earn <span className="text-red-600" data-id="element-395">$2,500</span> a<br data-id="element-396" />
                month while using
                <br data-id="element-397" />
                the equipment.
              </p>
            </div>

            <ChevronRight className="hidden md:block w-8 h-8 text-red-500 flex-shrink-0" data-id="element-398" />

            {/* Step 4 */}
            <div className="flex-1 text-center relative z-10" data-id="element-399">
              <div className="w-24 h-24 mx-auto mb-4 flex items-center justify-center" data-id="element-400">
                <PiggyBank className="w-16 h-16 text-gray-800" strokeWidth={1.5} data-id="element-401" />
              </div>
              <div className="bg-gray-200 py-2 px-4 mb-4 font-bold text-gray-700 text-sm uppercase tracking-wide" data-id="element-402">
                Profits
              </div>
              <p className="text-sm text-gray-600 uppercase font-semibold" data-id="element-403">
                So you pocket
                <br data-id="element-404" />
                <span className="text-red-600" data-id="element-405">$2,275</span> every month!
              </p>
            </div>
          </div>
        </section>

        {/* The Quickspark Advantage */}
        <section className="bg-gray-200 py-20 mb-24" data-id="element-406">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-407">
            <h2 className="text-3xl font-bold text-center text-gray-900 uppercase tracking-tight mb-12" data-id="element-408">
              The Quickspark Advantage
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-409">
              {/* Col 1 */}
              <div className="bg-gray-200 flex flex-col" data-id="element-410">
                <div className="bg-red-600 text-white text-center py-4 px-2 font-bold uppercase tracking-wide min-h-[80px] flex items-center justify-center" data-id="element-411">
                  Generate
                  <br data-id="element-412" />
                  Revenue
                </div>
                <div className="p-6 text-center text-sm text-gray-700 leading-relaxed" data-id="element-413">
                  Financing gives the option to get new equipment now and pay
                  for it in small, manageable payments. You can start earning
                  money with your new equipment before your first payment is
                  due!
                </div>
              </div>
              {/* Col 2 */}
              <div className="bg-gray-200 flex flex-col" data-id="element-414">
                <div className="bg-red-600 text-white text-center py-4 px-2 font-bold uppercase tracking-wide min-h-[80px] flex items-center justify-center" data-id="element-415">
                  Low
                  <br data-id="element-416" />
                  Payments
                </div>
                <div className="p-6 text-center text-sm text-gray-700 leading-relaxed" data-id="element-417">
                  QuickSpark offers monthly payments that range from 12 to 60
                  months and ensures the best fit for your business.
                </div>
              </div>
              {/* Col 3 */}
              <div className="bg-gray-200 flex flex-col" data-id="element-418">
                <div className="bg-red-600 text-white text-center py-4 px-2 font-bold uppercase tracking-wide min-h-[80px] flex items-center justify-center" data-id="element-419">
                  100% Tax
                  <br data-id="element-420" />
                  Deductible
                </div>
                <div className="p-6 text-center text-sm text-gray-700 leading-relaxed" data-id="element-421">
                  Section 179 allows businesses to deduct the full purchase
                  price for the equipment financed. Most businesses are able to
                  deduct 100% of the total equipment costs.
                </div>
              </div>
              {/* Col 4 */}
              <div className="bg-gray-200 flex flex-col" data-id="element-422">
                <div className="bg-red-600 text-white text-center py-4 px-2 font-bold uppercase tracking-wide min-h-[80px] flex items-center justify-center" data-id="element-423">
                  Lines Of
                  <br data-id="element-424" />
                  Credit Open
                </div>
                <div className="p-6 text-center text-sm text-gray-700 leading-relaxed" data-id="element-425">
                  Keep potential lines of credit open for financial emergencies
                  and use our financing to help build your business credentials
                  with any bank.
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Who Qualifies */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-24" data-id="element-426">
          <h2 className="text-3xl font-bold text-gray-900 uppercase tracking-tight mb-6" data-id="element-427">
            Who Qualifies?
          </h2>
          <p className="text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed" data-id="element-428">
            QuickSpark partners with multiple funding sources to find the best
            solution for your financial situation. Whether you have a low credit
            score or just starting your business out, we have special financing
            programs for every situation and will get you manageable low monthly
            payments.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto" data-id="element-429">
            <div className="bg-red-600 text-white py-3 px-6 font-bold uppercase tracking-wide rounded-sm shadow-sm" data-id="element-430">
              Startup Business
            </div>
            <div className="bg-red-600 text-white py-3 px-6 font-bold uppercase tracking-wide rounded-sm shadow-sm" data-id="element-431">
              Small/Medium Business
            </div>
            <div className="bg-red-600 text-white py-3 px-6 font-bold uppercase tracking-wide rounded-sm shadow-sm" data-id="element-432">
              Low Credit Score
            </div>
            <div className="bg-red-600 text-white py-3 px-6 font-bold uppercase tracking-wide rounded-sm shadow-sm" data-id="element-433">
              Established Business
            </div>
            <div className="bg-red-600 text-white py-3 px-6 font-bold uppercase tracking-wide rounded-sm shadow-sm" data-id="element-434">
              Bankruptcies
            </div>
            <div className="bg-red-600 text-white py-3 px-6 font-bold uppercase tracking-wide rounded-sm shadow-sm" data-id="element-435">
              High Credit Score
            </div>
          </div>
        </section>

        {/* Simple Application Process */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-24" data-id="element-436">
          <h2 className="text-3xl font-bold text-gray-900 uppercase tracking-tight mb-6" data-id="element-437">
            Simple Application Process
          </h2>
          <p className="text-gray-600 mb-16 max-w-2xl mx-auto leading-relaxed" data-id="element-438">
            With our simple electronic application, you can get an instant
            credit decision within hours of submitting. We can approve financing
            from $1,000 to $100,000.
          </p>

          <div className="relative flex flex-col md:flex-row justify-between items-start md:items-center gap-12 md:gap-4" data-id="element-439">
            {/* Connecting Line (Desktop) */}
            <div className="hidden md:block absolute top-12 left-1/6 right-1/6 h-2 bg-red-600 z-0" data-id="element-440"></div>
            <div className="hidden md:block absolute top-12 left-1/6 right-1/6 h-2 border-t-4 border-dotted border-white z-10" data-id="element-441"></div>

            {/* Step 1 */}
            <div className="flex-1 flex flex-col items-center relative z-20" data-id="element-442">
              <div className="w-24 h-24 rounded-full border-4 border-red-600 bg-white flex items-center justify-center mb-6 shadow-md" data-id="element-443">
                <Zap className="w-10 h-10 text-gray-800" fill="currentColor" data-id="element-444" />
              </div>
              <div className="text-4xl font-bold text-red-600 mb-4" data-id="element-445">1</div>
              <p className="text-xs font-bold text-gray-900 uppercase tracking-wide max-w-[200px]" data-id="element-446">
                Instant credit decision with absolutely no obligations
              </p>
            </div>

            {/* Step 2 */}
            <div className="flex-1 flex flex-col items-center relative z-20" data-id="element-447">
              <div className="w-24 h-24 rounded-full border-4 border-red-600 bg-white flex items-center justify-center mb-6 shadow-md" data-id="element-448">
                <FileSignature className="w-10 h-10 text-gray-800" data-id="element-449" />
              </div>
              <div className="text-4xl font-bold text-red-600 mb-4" data-id="element-450">2</div>
              <p className="text-xs font-bold text-gray-900 uppercase tracking-wide max-w-[200px]" data-id="element-451">
                Electronically sign finance documents
              </p>
            </div>

            {/* Step 3 */}
            <div className="flex-1 flex flex-col items-center relative z-20" data-id="element-452">
              <div className="w-24 h-24 rounded-full border-4 border-red-600 bg-white flex items-center justify-center mb-6 shadow-md" data-id="element-453">
                <Truck className="w-10 h-10 text-gray-800" data-id="element-454" />
              </div>
              <div className="text-4xl font-bold text-red-600 mb-4" data-id="element-455">3</div>
              <p className="text-xs font-bold text-gray-900 uppercase tracking-wide max-w-[200px]" data-id="element-456">
                When the documents are complete, order ships!
              </p>
            </div>
          </div>
        </section>

        {/* Get Started Today */}
        <section className="bg-gray-200 py-20" data-id="element-457">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-458">
            <h2 className="text-3xl font-bold text-gray-900 uppercase tracking-tight mb-6" data-id="element-459">
              Get Started Today
            </h2>
            <div className="w-16 h-1 bg-gray-400 mx-auto mb-8" data-id="element-460"></div>
            <p className="text-gray-700 mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-461">
              We strive to deliver a seamless experience so you can focus on
              what matters the most, your bottom line! We've helped thousands of
              applicants ignite their business and are proud to be the trusted
              source in financing solutions.
            </p>
            <Button className="bg-red-600 hover:bg-red-700 text-white px-10 py-4 text-lg font-bold uppercase tracking-wide rounded-sm" data-id="element-462">
              Apply Now
            </Button>
          </div>
        </section>
      </main>

      <Footer data-id="element-463" />
    </div>;
}